import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  Text,
  Alert,
  AlertIcon,
  HStack,
} from '@chakra-ui/react'
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react'
import { z } from 'zod'
import { useAuthStore } from '@/store/auth'
import { useThemeColors } from '@/hooks/useThemeColors'

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export function ForgotPasswordForm() {
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const { forgotPassword, isLoading } = useAuthStore()
  const colors = useThemeColors()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setError('')
      await forgotPassword(data.email)
      setIsSuccess(true)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.')
    }
  }

  if (isSuccess) {
    return (
      <Box minH="100vh" bg={colors.bg} display="flex" alignItems="center" justifyContent="center">
        <Card maxW="md" w="full" bg={colors.cardBg}>
          <CardBody p={8}>
            <VStack spacing={6}>
              <Box textAlign="center">
                <Box mb={4} display="flex" justifyContent="center">
                  <img src="/assets/img/deltrans-logo.png" alt="Deltrans Logo" style={{ maxWidth: '300px', height: 'auto' }} />
                </Box>

                <CheckCircle size={64} color="#48BB78" />
                <Heading size="lg" color="green.500" mb={2} mt={4}>
                  Check Your Email
                </Heading>
                <Text color={colors.text}>
                  We&apos;ve sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.
                </Text>
              </Box>

              <VStack spacing={4} w="full">
                <Button
                  bg={colors.buttonColor}
                  color="white"
                  _hover={{ bg: 'blue.600' }}
                  size="lg"
                  w="full"
                  onClick={() => navigate('/login')}
                >
                  Back to Login
                </Button>

                <Text fontSize="sm" color={colors.textSecondary} textAlign="center">
                  Didn&apos;t receive the email? Check your spam folder or{' '}
                  <Button
                    variant="link"
                    color="blue.500"
                    fontSize="sm"
                    onClick={() => setIsSuccess(false)}
                    _hover={{ textDecoration: 'underline' }}
                  >
                    try again
                  </Button>
                </Text>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    )
  }

  return (
    <Box minH="100vh" bg={colors.bg} display="flex" alignItems="center" justifyContent="center">
      <Card maxW="md" w="full" bg={colors.cardBg}>
        <CardBody p={8}>
          <VStack spacing={6}>
            <Box textAlign="center">
              <Box mb={4} display="flex" justifyContent="center">
                <img src="/assets/img/deltrans-logo.png" alt="Deltrans Logo" style={{ maxWidth: '300px', height: 'auto' }} />
              </Box>

              <Heading size="lg" color="blue.500" mb={2}>
                Forgot Password?
              </Heading>
              <Text color={colors.text}>Enter your email address and we&apos;ll send you a link to reset your password.</Text>
            </Box>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Box as="form" onSubmit={handleSubmit(onSubmit)} w="full">
              <VStack spacing={4}>
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel>Email Address</FormLabel>
                  <InputGroup>
                    <InputLeftElement>
                      <Mail size={20} />
                    </InputLeftElement>
                    <Input {...register('email')} type="email" placeholder="Enter your email address" />
                  </InputGroup>
                  <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                </FormControl>

                <Button
                  type="submit"
                  bg={colors.buttonColor}
                  color="white"
                  _hover={{ bg: 'blue.600' }}
                  size="lg"
                  w="full"
                  isLoading={isLoading}
                  loadingText="Sending reset link..."
                >
                  Send Reset Link
                </Button>
              </VStack>
            </Box>

            <HStack justify="center" mt={4}>
              <Button
                variant="link"
                color={colors.link}
                fontSize="sm"
                onClick={() => navigate('/login')}
                leftIcon={<ArrowLeft size={16} />}
                _hover={{ textDecoration: 'underline' }}
              >
                Back to Login
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}

